import React, { useState } from 'react';
import { Row, Col, Card, Form, Button, Alert, Table } from 'react-bootstrap';
import { useUI } from '../context/UIContext';
import { useBrand } from '../context/AuthContext';
import { reportService } from '../services/reportService';
import { formatDate, formatDateForApi } from '../utils/formatters';
import BenefitStatusBadge from '../utils/BenefitStatusBadge';
import { getFundTypeLabel } from '../utils/fundTypeUtils';
import { buildBenefitReportHeaders, REPORT_TYPE } from '../utils/benefitReportHeaders';
import { FUND_TYPES } from '../constants/constants';
import { ROUTES } from '../constants/routes';

const BenefitHistoryReportPage = () => {
  const { showLoading, hideLoading } = useUI();
  const { isCNG } = useBrand();

  // Search form state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [accountId, setAccountId] = useState('');
  const [agencyName, setAgencyName] = useState('');

  // Results state
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setSearched(true);
    showLoading('Searching...');

    try {
      const response = await reportService.searchBenefitHistory({
        startDate: formatDateForApi(startDate),
        endDate: formatDateForApi(endDate),
        accountId,
        agencyName,
      });

      if (response.success) {
        setResults(response.benefits || []);
      } else if (response.validationErrors) {
        setError(response.validationErrors.join(' '));
        setResults(null);
      } else {
        setError(response.error || 'Search failed.');
        setResults(null);
      }
    } catch (err) {
      console.error('Error searching benefit history:', err);
      setError('An error occurred while searching.');
      setResults(null);
    } finally {
      hideLoading();
    }
  };

  const exportToCSV = () => {
    if (!results || results.length === 0) return;

    const headers = buildBenefitReportHeaders(REPORT_TYPE.HISTORY, isCNG);

    const rows = results.map((benefit) => {
      const row = [
        benefit.status || '',
        benefit.agencyEmail || '',
        formatDate(benefit.createTstamp),
        formatDate(benefit.updateTstamp),
        benefit.agencyName || '',
        benefit.submissionType || '',
        benefit.lastName || '',
        benefit.firstName || '',
        benefit.accountId || '',
        benefit.street || '',
        benefit.city || '',
        benefit.state || '',
        benefit.zip || '',
        getFundTypeLabel(benefit.fundType),
        benefit.pledgeAmount || '',
        benefit.priorPledgeAmount || '',
      ];

      if (isCNG) {
        row.push(
          benefit.fpl || '',
          benefit.smi || '',
          benefit.ami || '',
          benefit.energyDiscount || '',
          benefit.priorEnergyDiscount || '',
          benefit.arrearage || '',
          benefit.fundTypeString === FUND_TYPES.CARES ? (benefit.weatherizationProgramInterest ? 'Yes' : 'No') : ''
        );
      }

      row.push(
        benefit.additionalInformation || '',
        benefit.reasonForChange || '',
        benefit.submitted ? 'Yes' : 'No'
      );

      return row;
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'benefit_history_results.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="benefit-history-report-page">
      <div className="d-flex justify-content-center mb-3">
          <h2>Benefit History Report</h2>
        </div>

        {/* Search Form */}
        <Card className="shadow mb-4">
          <Card.Body>
            <Form onSubmit={handleSearch}>
              {error && (
                <Alert variant="danger">{error}</Alert>
              )}

              <Row>
                <Col md={3} className="mb-3">
                  <Form.FloatingLabel label="Start Date" controlId="start-date">
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Form.FloatingLabel>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.FloatingLabel label="End Date" controlId="end-date">
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Form.FloatingLabel>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.FloatingLabel label="Search by Account ID" controlId="account-id">
                  <Form.Control
                    type="text"
                    placeholder="10-digit account"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    maxLength={10}
                  />
                  </Form.FloatingLabel>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.FloatingLabel label="Search by Agency Name" controlId="agency-name">
                  <Form.Control
                    type="text"
                    placeholder="Min 2 characters"
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                  />
                  </Form.FloatingLabel>
                </Col>
              </Row>

              <Button type="submit" variant="primary">
                Search
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {/* Results */}
        <Card className="shadow">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Results {results && `(${results.length})`}</h5>
            {results && results.length > 0 && (
              <Button variant="info" size="sm" onClick={exportToCSV}>
                Export Results (CSV)
              </Button>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            {!searched ? (
              <div className="p-4 text-muted">No search performed yet</div>
            ) : results && results.length === 0 ? (
              <div className="p-4 text-muted">Query returned no results</div>
            ) : results && results.length > 0 ? (
              <Table striped bordered hover size="sm" responsive className="mb-0">
                  <thead className="sticky-header">
                    <tr>
                      <th>Status</th>
                      <th>Agency Email</th>
                      <th>Date Entered</th>
                      <th>Audit Date</th>
                      <th>Agency Name</th>
                      <th>New/Corrected</th>
                      <th>Last Name</th>
                      <th>First Name</th>
                      <th>Account Number</th>
                      <th>Service Address</th>
                      <th>Town</th>
                      <th>State</th>
                      <th>Zip</th>
                      <th>Fund</th>
                      <th>Grant</th>
                      <th>Prior Grant</th>
                      {isCNG && (
                        <>
                          <th>FPL</th>
                          <th>SMI</th>
                          <th>AMI</th>
                          <th>Energy Discount</th>
                          <th>Prior Discount</th>
                          <th>Arrearage Mgmt</th>
                          <th>Weatherization</th>
                        </>
                      )}
                      <th>Additional Info</th>
                      <th>Reason for Change</th>
                      <th>Uploaded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((benefit, idx) => (
                      <tr key={benefit.id || idx}>
                        <td><BenefitStatusBadge status={benefit.status} /></td>
                        <td>{benefit.agencyEmail}</td>
                        <td>{formatDate(benefit.createTstamp)}</td>
                        <td>{formatDate(benefit.updateTstamp)}</td>
                        <td>{benefit.agencyName}</td>
                        <td>{benefit.submissionType}</td>
                        <td>{benefit.lastName}</td>
                        <td>{benefit.firstName}</td>
                        <td>{benefit.accountId}</td>
                        <td>{benefit.street}</td>
                        <td>{benefit.city}</td>
                        <td>{benefit.state}</td>
                        <td>{benefit.zip}</td>
                        <td>{getFundTypeLabel(benefit.fundType)}</td>
                        <td>{benefit.pledgeAmount}</td>
                        <td>{benefit.priorPledgeAmount}</td>
                        {isCNG && (
                          <>
                            <td>{benefit.fpl}</td>
                            <td>{benefit.smi}</td>
                            <td>{benefit.ami}</td>
                            <td>{benefit.energyDiscount}</td>
                            <td>{benefit.priorEnergyDiscount}</td>
                            <td>{benefit.arrearage}</td>
                            <td>
                              {benefit.fundType === FUND_TYPES.CARES
                                ? benefit.weatherizationProgramInterest
                                  ? 'Yes'
                                  : 'No'
                                : ''}
                            </td>
                          </>
                        )}
                        <td className="text-truncate" style={{ maxWidth: '150px' }} title={benefit.additionalInformation}>
                          {benefit.additionalInformation}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: '150px' }} title={benefit.reasonForChange}>
                          {benefit.reasonForChange}
                        </td>
                        <td>{benefit.submitted ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
            ) : null}
              </Card.Body>
        </Card>
    </div>
  );
};

export default BenefitHistoryReportPage;
